import { type CSSProperties } from "react";
import { Dialog } from "../components";
import { GameLayout } from "./shared/GameLayout";
import {
  GameArt,
  PrivateInfo,
  SubmitButton,
  TextAction,
  draftScope,
} from "./shared/PresenterParts";
import { useDraft } from "./shared/drafts";
import { spectrumFinalVerdict, spectrumRoundVerdict } from "./outcome";
import { PrivateTrigger } from "./shared/PrivatePanel";
import { playerName, type PlayProps } from "./types";
import { publicTableModel } from "./public-presentation";
export function Spectrum(props: PlayProps) {
  const { state, send, isDisplay } = props,
    g = state.game,
    p = g.public,
    s = g.private,
    players = p.players ?? [],
    n = (id: string) => playerName(players, id),
    can = (a: string) =>
      !isDisplay && !state.isSpectator && g.legalActions.includes(a),
    scope = draftScope(state),
    [value, setValue] = useDraft(scope, "guess:value", 50),
    [clueOpen, setClueOpen] = useDraft(scope, "clue:open", false),
    reveal = ["reveal", "finished"].includes(g.phase) ? p.lastReveal : null,
    publicAudience = isDisplay || state.isSpectator;
  // After the reveal only: this seat's own guess, marked "You" on the scale.
  const ownValue: number | null =
    reveal && !publicAudience
      ? (reveal.guesses?.find((v: any) => v.playerId === state.playerId)
          ?.value ??
        (typeof s.ownGuess === "number" ? s.ownGuess : null))
      : null;
  // Fan out pins that would overlap: each pin takes the first row where it
  // clears the previous pin by a few points.
  // A small beeswarm: up to four rows under the scale; a pin that finds every
  // row taken near its value steps sideways, alternating out from the value.
  const pins: { playerId: string; value: number; x: number; row: number }[] =
    [];
  if (reveal) {
    const sorted = [...(reveal.guesses ?? [])].sort(
      (a: any, b: any) => a.value - b.value,
    );
    // Your pin first, so it sits closest to the true value.
    sorted.sort(
      (a: any, b: any) =>
        Number(b.playerId === state.playerId) -
        Number(a.playerId === state.playerId),
    );
    const clear = (x: number, row: number) =>
      pins.every((p) => p.row !== row || Math.abs(p.x - x) >= 4);
    for (const v of sorted) {
      let placed = false;
      for (let step = 0; step < 40 && !placed; step++) {
        const offset = Math.ceil(step / 2) * 3 * (step % 2 ? 1 : -1);
        const x = Math.max(0, Math.min(100, v.value + offset));
        for (let row = 0; row < 4 && !placed; row++)
          if (clear(x, row)) {
            pins.push({ playerId: v.playerId, value: v.value, x, row });
            placed = true;
          }
      }
      if (!placed)
        pins.push({ playerId: v.playerId, value: v.value, x: v.value, row: 3 });
    }
  }
  const pinRows = pins.length ? Math.max(...pins.map((v) => v.row)) + 1 : 0;
  const verdict = g.finished
    ? spectrumFinalVerdict(p.totalScore ?? 0, p.maximumScore ?? 0)
    : reveal
      ? spectrumRoundVerdict(reveal.points, p.scoringBands)
      : null;
  const title = g.finished
    ? "Final score."
    : reveal
      ? `+${reveal.points} points.`
      : can("clue")
        ? "Give a clue."
        : g.phase === "clue"
          ? "A clue is coming."
          : can("guess")
            ? "Where does it belong?"
            : !isDisplay &&
                state.playerId !== p.guideId &&
                s.ownGuess !== null &&
                s.ownGuess !== undefined
              ? "Your guess is locked in."
              : "The team is guessing.";
  return (
    <GameLayout
      {...props}
      title={title}
      progressLabel="guesses locked"
      publicTitle={
        g.finished
          ? title
          : g.phase === "guess"
            ? "The team is guessing."
            : g.phase === "clue"
              ? `${n(p.guideId)} is the guide.`
              : title
      }
      description={
        reveal
          ? `Target ${reveal.target} · team median ${reveal.groupGuess} · ${reveal.error} away.`
          : g.phase === "clue"
            ? !isDisplay && !state.isSpectator && state.playerId === p.guideId
              ? "You’re the guide this round."
              : `${n(p.guideId)} is the guide.`
            : null
      }
      publicDescription={
        reveal && !g.finished
          ? `Target ${reveal.target} · team median ${reveal.groupGuess} · ${reveal.error} away.`
          : null
      }
      phaseLabel={
        g.finished ? "Final score" : `Round ${g.round} of ${p.rounds ?? g.round}`
      }
      phaseNote={`Team score ${p.totalScore ?? 0} / ${p.maximumScore ?? 0}`}
      board={
        <>
          {g.phase === "clue" || g.finished ? (
            <GameArt kind="spectrum" />
          ) : null}
          {p.clue && !g.finished ? (
            <button
              type="button"
              className="play-spectrum-clue"
              data-tour="clue-card"
              aria-label="Read full clue"
              onClick={() => setClueOpen(true)}
            >
              <span className="play-excerpt">“{p.clue}”</span>
              <cite>Clue from {n(p.guideId)}</cite>
            </button>
          ) : null}
          {!can("guess") ? (
            <div className="spectrum-scale" data-tour="scale">
              <div className="play-scale-endpoints">
                <strong>{p.scale?.left}</strong>
                <strong>{p.scale?.right}</strong>
              </div>
              <div
                className="play-scale"
                role="img"
                aria-label={
                  reveal
                    ? `Target ${reveal.target}, team median ${reveal.groupGuess}`
                    : "Scale from 0 to 100"
                }
              >
                {reveal ? (
                  <>
                    <i
                      className="play-target-marker"
                      style={{ left: `${reveal.target}%` }}
                    />
                    <i
                      className="play-median-marker"
                      style={{ left: `${reveal.groupGuess}%` }}
                    />
                  </>
                ) : null}
              </div>
              {reveal && pins.length ? (
                <div
                  className="spectrum-pins"
                  style={{ "--rows": pinRows } as CSSProperties}
                  role="list"
                  aria-label="Every guess"
                >
                  {pins.map((v) => {
                    const mine =
                      ownValue !== null && v.playerId === state.playerId;
                    return (
                      <span
                        role="listitem"
                        key={v.playerId}
                        className={`spectrum-pin${mine ? " is-you" : ""}`}
                        style={
                          {
                            left: `${v.x}%`,
                            "--row": v.row,
                          } as CSSProperties
                        }
                        title={`${n(v.playerId)} · ${v.value}`}
                      >
                        <span className="sr-only">
                          {mine ? "You" : n(v.playerId)}: {v.value}
                        </span>
                        {mine ? (
                          <b aria-hidden="true">You</b>
                        ) : null}
                      </span>
                    );
                  })}
                </div>
              ) : null}
              <div className="play-scale-labels" aria-hidden="true">
                <span>0</span>
                <span>50</span>
                <span>100</span>
              </div>
            </div>
          ) : null}
          {verdict ? (
            <p className="spectrum-verdict" data-tour="verdict">
              <strong>{verdict}</strong>
              {reveal && !g.finished && ownValue !== null ? (
                <span>
                  You guessed {ownValue} · target {reveal.target}
                </span>
              ) : null}
            </p>
          ) : null}
          {g.phase === "guess" ? (
            <p className="table-progress" role="status">
              {publicTableModel(g, state.players ?? []).progress?.submitted ??
                0}{" "}
              of{" "}
              {publicTableModel(g, state.players ?? []).progress?.eligible ?? 0}{" "}
              guesses locked
            </p>
          ) : null}
          {reveal ? (
            <ul
              className="spectrum-legend"
              aria-label="Legend"
              data-tour="legend"
            >
              <li>
                <i className="legend-target" aria-hidden="true" />
                Target {reveal.target}
              </li>
              <li>
                <i className="legend-median" aria-hidden="true" />
                Team {reveal.groupGuess}
              </li>
              <li>
                <i className="legend-guess" aria-hidden="true" />
                Guesses
              </li>
              {ownValue !== null ? (
                <li>
                  <i className="legend-you" aria-hidden="true" />
                  You {ownValue}
                </li>
              ) : null}
            </ul>
          ) : null}
          {clueOpen ? (
            <Dialog title="The guide’s clue" onClose={() => setClueOpen(false)}>
              <p className="play-full-clue">“{p.clue}”</p>
              <p className="play-help-copy">From {n(p.guideId)}</p>
            </Dialog>
          ) : null}
        </>
      }
      action={
        can("clue") ? (
          <PrivateTrigger>Open your target & clue</PrivateTrigger>
        ) : can("guess") ? (
          <form
            className="play-guess-form"
            data-tour="guess-scale"
            onSubmit={(e) => {
              e.preventDefault();
              send({ type: "guess", value });
            }}
          >
            <div className="play-scale-endpoints">
              <strong>{p.scale?.left}</strong>
              <strong>{p.scale?.right}</strong>
            </div>
            <div
              className="play-range-wrap"
              style={{ "--value": value } as CSSProperties}
            >
              <output className="play-draft-label">{value}</output>
              <input
                type="range"
                aria-label="Your guess on the scale"
                min={0}
                max={100}
                step={1}
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
              />
            </div>
            <div className="play-scale-labels" aria-hidden="true">
              <span>0</span>
              <span>50</span>
              <span>100</span>
            </div>
            <label className="play-exact-position">
              Exact position
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                step={1}
                value={value}
                onChange={(e) =>
                  setValue(
                    Math.max(
                      0,
                      Math.min(100, Math.round(Number(e.target.value))),
                    ),
                  )
                }
              />
            </label>
            <SubmitButton>Lock in {value}</SubmitButton>
          </form>
        ) : null
      }
      privateContent={
        // Guessers hold no private information: no "Your card" entry for them.
        s.role === "guide" ? <PrivateInfo state={state} /> : undefined
      }
      privateAction={
        can("clue") ? (
          <TextAction
            label="Your clue"
            type="clue"
            placeholder="A concrete example, not a number…"
            send={send}
            scope={scope}
          />
        ) : undefined
      }
    />
  );
}
